import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { readImageSize } from '../utils/profileFrameRules';
import { checkCoverFile, COVER_SLOT_TYPES, COVER_SLOTS, CoverSlotType, ShopMediaAsset } from '../utils/premiumBundleRules';
import { authHeaders, uploadWalletAsset } from '../utils/walletShopApi';

type Props = {
  productKey: string;
  mediaAssets: ShopMediaAsset[];
  onChange: (next: ShopMediaAsset[]) => void;
};

/**
 * Cover artwork for premium bundles and sticker packs: a required WebP `thumbnail` and an optional
 * `image`. Each file is checked (extension, MIME, size), then uploaded straight to R2 through the
 * same presigned flow as other products, and replaces that slot in MediaAssets.
 */
const CoverMediaEditor: React.FC<Props> = ({ productKey, mediaAssets, onChange }) => {
  const [uploadingSlot, setUploadingSlot] = useState<CoverSlotType | ''>('');

  const handleFile = async (slotType: CoverSlotType, file: File | undefined) => {
    if (!file) return;
    const problem = checkCoverFile(slotType, file);
    if (problem) {
      toast.error(problem);
      return;
    }
    if (!productKey.trim()) {
      toast.error('Enter a ProductKey before uploading the cover (files are stored under it).');
      return;
    }
    const auth = authHeaders();
    if ('error' in auth) {
      toast.error(auth.error);
      return;
    }
    setUploadingSlot(slotType);
    try {
      const size = await readImageSize(file).catch(() => undefined);
      const asset = await uploadWalletAsset(auth.headers, productKey, slotType, file, size);
      onChange([...mediaAssets.filter((a) => a.Type !== slotType), asset]);
      toast.success(`${COVER_SLOTS[slotType].label} uploaded — save the product to keep it.`);
    } catch (error) {
      const message = (error as Error).message || 'Upload failed';
      toast.error(message.toLowerCase().includes('failed to fetch') ? 'Upload failed (possible CORS or network issue).' : message);
    } finally {
      setUploadingSlot('');
    }
  };

  return (
    <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
      {COVER_SLOT_TYPES.map((slotType) => {
        const slot = COVER_SLOTS[slotType];
        const asset = mediaAssets.find((a) => a.Type === slotType);
        return (
          <div key={slotType} className='bg-black/20 border border-white/10 rounded-lg p-3 space-y-2'>
            <div className='flex items-center justify-between'>
              <span className='text-xs font-semibold text-white'>
                {slot.label} {slotType === 'thumbnail' && <span className='text-amber-400'>*</span>}
              </span>
              <span className={`text-[10px] ${asset ? 'text-emerald-300' : 'text-gray-500'}`}>{asset ? 'attached' : 'missing'}</span>
            </div>
            <p className='text-[11px] text-gray-400'>{slot.hint}</p>
            {asset && (
              <div className='flex items-start gap-2'>
                <img
                  src={asset.Url}
                  alt={slot.label}
                  className='h-20 w-20 object-contain rounded border border-white/10 bg-[repeating-conic-gradient(#333_0_25%,#222_0_50%)] bg-[length:12px_12px]'
                />
                <div className='min-w-0 space-y-1'>
                  <a href={asset.Url} target='_blank' rel='noreferrer' className='text-[11px] text-[#FF9C15] hover:underline break-all'>
                    {asset.Url.split('/').pop()}
                  </a>
                  {asset.Width && asset.Height && (
                    <div className='text-[10px] text-gray-500'>
                      {asset.Width}×{asset.Height}
                    </div>
                  )}
                  <button
                    type='button'
                    onClick={() => onChange(mediaAssets.filter((a) => a.Type !== slotType))}
                    className='text-[11px] text-red-300 hover:text-red-200'
                  >
                    Remove
                  </button>
                </div>
              </div>
            )}
            <input
              type='file'
              accept={slot.accept}
              disabled={!!uploadingSlot}
              onChange={(e) => {
                handleFile(slotType, e.target.files?.[0]);
                e.target.value = '';
              }}
              className='w-full text-xs text-gray-300'
            />
            {uploadingSlot === slotType && <p className='text-[11px] text-amber-300'>Uploading...</p>}
          </div>
        );
      })}
    </div>
  );
};

export default CoverMediaEditor;
